// ========================================
// FILE 1: Questionnaire.js (FIXED)
// ========================================

import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import CommonButton from "../../Components/CommonButton";
import CommonDropdown from "../../Components/CommonDropdown";
import CommonModal from "../../Components/CommonModal";
import StatusBadge from "../../Components/StatusBadge";
import ScreenLoader from "../../Components/ScreenLoader";
import ButtonLoader from "../../Components/ButtonLoader";
import {
    check_with_nova,
    submit_framework_question_answer,
    upload_document_to_check_with_nova,
    user_assign_domains_question_list,
    getOrganizationComplianceSubmissionService,
    organizationComplianceSubmissionService,
    setDraftStatusService,
} from "../../services";

import QuestionCard from "./QuestionCard";
import NovaPanel from "./NovaPanel";

function Questionnaire() {
    const [responses, setResponses] = useState({});
    const [domainData, setDomainData] = useState([]);
    const [screenLoader, setScreenLoader] = useState(false);

    const [checkingId, setCheckingId] = useState(null);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);

    const [novaError, setNovaError] = useState("");
    const [novaData, setNovaData] = useState(null);

    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [showEditRestrictionModal, setShowEditRestrictionModal] = useState(false);
    const [submittingStatus, setSubmittingStatus] = useState(false);
    const [settingDraftStatus, setSettingDraftStatus] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [modalType, setModalType] = useState("success"); // 'success' or 'error'

    const [error, setError] = useState('');
    const [resMsg, setResMsg] = useState("");
    const [messageQuestionId, setMessageQuestionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDomainId, setSelectedDomainId] = useState("");
    const [selectedControlId, setSelectedControlId] = useState("");
    const [selectedReviewerResponse, setSelectedReviewerResponse] = useState("");
    const [showSharedQuestions, setShowSharedQuestions] = useState(false);
    const [sharedQuestions, setSharedQuestions] = useState([]);
    const [isEmptyQue, setIsEmptyQue] = useState(false);

    // FIX 1: Use useCallback to prevent function recreation
    const updateResponse = useCallback((id, field, value) => {
        setResponses((prev) => {
            // Only update if value actually changed
            if (prev[id]?.[field] === value) {
                return prev;
            }

            return {
                ...prev,
                [id]: {
                    ...prev[id],
                    [field]: value,
                    submitted: false
                },
            };
        });
        setError('');
        setResMsg('');
        setMessageQuestionId(null);
    }, []);

    useEffect(() => {
        if (!domainData?.length) return;
        setResponses((prev) => {
            let changed = false;
            const next = { ...prev };

            domainData.forEach((domain) => {
                domain?.controls?.forEach((control) => {
                    control?.questions?.forEach((q) => {
                        const qid = q?.control_question_id;
                        if (!qid || next[qid]) return;

                        const questionAnswerBlock = q?.question_answer || q?.answer_details || {};
                        const existingAnswer =
                            q?.answer_response ||
                            q?.user_response ||
                            questionAnswerBlock?.answer_response ||
                            questionAnswerBlock?.user_response ||
                            questionAnswerBlock?.userResponse ||
                            "";
                        const existingComments =
                            q?.user_answer ||
                            q?.answer ||
                            questionAnswerBlock?.user_answer ||
                            questionAnswerBlock?.answer ||
                            questionAnswerBlock?.comments ||
                            "";
                        const evidenceSource =
                            q?.evidence_submitted ||
                            q?.evidences ||
                            questionAnswerBlock?.evidence_submitted ||
                            questionAnswerBlock?.evidences ||
                            [];
                        const evidenceUrls = evidenceSource
                            ?.map((evidence) => evidence?.url)
                            ?.filter(Boolean) || [];

                        if (
                            (existingAnswer && existingAnswer.trim() !== "") ||
                            (existingComments && existingComments.trim() !== "") ||
                            evidenceUrls.length > 0
                        ) {
                            next[qid] = {
                                answer: existingAnswer,
                                comments: existingComments,
                                file_urls: evidenceUrls,
                                files: [],
                                submitted: true
                            };
                            changed = true;
                        }
                    });
                });
            });

            return changed ? next : prev;
        });
    }, [domainData]);

    // Fetch domain questions
    const fetchQuestionList = async () => {
        const _selectedFrameworkId = localStorage.getItem('selectedFrameworkId')
        try {
            setScreenLoader(true);
            let query = `?framework_id=${_selectedFrameworkId}`;
            if (selectedReviewerResponse) {
                query += `&reviewer_response=${selectedReviewerResponse}`;
            }
            const response = await user_assign_domains_question_list(query);
            const data = response?.statusCode?.response_data?.data?.domains || [];
            const sharedQuestionsData = response?.statusCode?.response_data?.data?.shared_questions || [];
            if (data.length == 0) {
                setIsEmptyQue(true);
                setShowSharedQuestions(true)
            }
            else {
                setIsEmptyQue(false);
            }
            setDomainData(data);
            setSharedQuestions(sharedQuestionsData);
        } finally {
            setScreenLoader(false);
        }
    };

    useEffect(() => {
        fetchQuestionList();
    }, [selectedReviewerResponse]);

    // Check for selection_id in localStorage and call API
    useEffect(() => {
        const selectionId = localStorage.getItem('selection_id');
        if (selectionId) {
            const fetchSubmissionData = async () => {
                try {
                    const params = `${selectionId}/submission/`;
                    const response = await getOrganizationComplianceSubmissionService(params);
                    setSubmissionStatus(response?.statusCode?.response_data?.data?.submission_status || null);
                } catch (error) {
                    console.error('Error fetching submission data:', error);
                }
            };
            fetchSubmissionData();
        }
    }, []);

    // FIX 2: Optimize file upload with useCallback
    const handleFileUpload = useCallback(async (id, file, orgId) => {
        if (!file) return;

        // Optimistically update UI first
        setResponses((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                files: [...(prev[id]?.files || []), file]
            }
        }));

        setSelectedQuestionId(id);
        setError('');

        const formData = new FormData();
        formData.append("file", file);
        formData.append("document_identifier", id);
        formData.append("organization_id", orgId);

        try {
            const upload = await upload_document_to_check_with_nova(formData);
            const url = upload?.statusCode?.response_data?.data?.s3_url || "";
            const user_document_id = upload?.statusCode?.response_data?.data?.user_document_id || "";

            setResponses((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    user_document_id,
                    file_urls: [...(prev[id]?.file_urls || []), url]
                }
            }));
        } catch (err) {
            console.error("File Upload Failed:", err);
            // Remove file from UI if upload failed
            setResponses((prev) => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    files: (prev[id]?.files || []).filter(f => f !== file)
                }
            }));
        }
    }, []);

    //  FIX 3: Optimize remove file with useCallback
    const removeFile = useCallback((id, index) => {
        setResponses((prev) => {
            const { files = [], file_urls = [] } = prev[id] || {};
            return {
                ...prev,
                [id]: {
                    ...prev[id],
                    files: files.filter((_, i) => i !== index),
                    file_urls: file_urls.filter((_, i) => i !== index)
                }
            };
        });
    }, []);

    // Check With Foresee
    const checkWithNovaHandler = useCallback(async (questionId) => {
        const selected = responses[questionId];

        setSelectedQuestionId(questionId);
        setCheckingId(questionId);
        setNovaError("");
        setNovaData(null);

        if (!selected?.comments) {
            setNovaError("Please provide an answer before checking with Foresee.");
            setCheckingId(null);
            return;
        }

        const params = {
            question_id: questionId,
            answer: selected.comments,
            files: selected.file_urls || [],
        };

        try {
            const res = await check_with_nova(params);
            const successMessage = res?.statusCode?.response_data?.data;
            const errorMessage =
                res?.errors?.message ||
                res?.error?.message ||
                res?.data?.error ||
                null;

            if (successMessage) {
                setNovaData(successMessage);
            } else if (errorMessage) {
                setNovaError(errorMessage);
            } else {
                setNovaError("Invalid Foresee response.");
            }
        } catch (err) {
            console.error(err);
            setNovaError("Foresee could not process your request. Try again.");
        } finally {
            setCheckingId(null);
        }
    }, [responses]);

    // Submit QuestionAnswer
    const submitQuestionAnswerHandler = useCallback(async (questionId) => {
        if (submissionStatus && submissionStatus !== "draft" && submissionStatus !== "rejected") {
            setShowEditRestrictionModal(true);
            return;
        }

        try {
            let payload = null;
            setLoading(true);
            setError('');
            setResMsg('');
            setMessageQuestionId(questionId || null);

            // First check if it's a shared question
            if (questionId) {
                const sharedQuestion = sharedQuestions.find(q => q?.control_question_id === questionId || q?.question?.control_question_id === questionId);
                if (sharedQuestion) {
                    const qid = sharedQuestion?.control_question_id || sharedQuestion?.question?.control_question_id;
                    const resp = responses[qid];
                    if (!resp) {
                        setError('Please select Yes / No / Partial before saving.1');
                        setLoading(false);
                        return;
                    }

                    const hasAnswer = resp.answer && resp.answer.trim() !== '';
                    const hasComments = resp.comments && resp.comments.trim() !== '';
                    const validAnswers = ["Yes", "No", "Partial"];
                    if (!hasAnswer || !validAnswers.includes(resp.answer)) {
                        setError('Please select Yes / No / Partial before saving.2');
                        setLoading(false);
                        return;
                    }

                    if (!hasAnswer && !hasComments) {
                        setError('Please answer the question to submit');
                        setLoading(false);
                        setMessageQuestionId(questionId);
                        return;
                    }

                    const evidences = (resp.file_urls || []).map((url) => ({
                        url: url,
                    }));


                    let orgDomainId = sharedQuestion?.organization_domain_id ||
                        sharedQuestion?.organization_domain_id ||
                        sharedQuestion?.organization?.organization_domain_id ||
                        sharedQuestion?.organization?.id ||
                        sharedQuestion?.domain?.organization_domain_id ||
                        sharedQuestion?.domain?.id ||
                        sharedQuestion?.organization_domain?.organization_domain_id ||
                        sharedQuestion?.question?.organization_domain_id ||
                        sharedQuestion?.control?.organization_domain_id ||
                        sharedQuestion?.control?.organization_domain_control_id ||
                        sharedQuestion?.domain_id ||
                        null;

                    // If still not found, try to find it from domainData by matching question
                    if (!orgDomainId) {

                        for (const domain of domainData || []) {
                            for (const control of domain?.controls || []) {
                                for (const q of control?.questions || []) {
                                    if (q?.control_question_id === qid) {
                                        orgDomainId = domain?.organization_domain_id;
                                        console.log("Found organization_domain_id from domainData:", orgDomainId);
                                        break;
                                    }
                                }
                                if (orgDomainId) break;
                            }
                            if (orgDomainId) break;
                        }
                    }

                    // If still not found, check if shared question has a domain reference we can use
                    if (!orgDomainId && sharedQuestion?.domain) {
                        // Try to find the domain in domainData by matching domain name or ID
                        const sharedDomain = sharedQuestion.domain;
                        for (const domain of domainData || []) {
                            if (domain?.domain?.id === sharedDomain?.id ||
                                domain?.domain?.name === sharedDomain?.name ||
                                domain?.domain_id === sharedDomain?.id) {
                                orgDomainId = domain?.organization_domain_id;
                                console.log("Found organization_domain_id from domain match:", orgDomainId);
                                break;
                            }
                        }
                    }

                    // Last resort: use first available domain's organization_domain_id
                    if (!orgDomainId && domainData?.length > 0) {
                        orgDomainId = domainData[0]?.organization_domain_id;
                        console.log("Using first domain's organization_domain_id as fallback:", orgDomainId);
                    }

                    console.log("Final extracted organization_domain_id:", orgDomainId);

                    // answer field should have comments if available, otherwise use user_response
                    const answerValue = hasComments ? resp.comments : (hasAnswer ? resp.answer : "");

                    // Build payload for shared question
                    const sharedPayload = {
                        organization_domain_id: orgDomainId,
                        question_id: qid,
                        user_document_id: resp.user_document_id || null,
                        answer: answerValue,
                        user_response: hasAnswer ? resp.answer : "",
                        evidences: evidences.length > 0 ? evidences : [{
                            url: "",
                        }]
                    };

                    // Add question_sharing_id only if it exists in shared question
                    if (sharedQuestion?.question_sharing_id || sharedQuestion?.organization_domain_id) {
                        sharedPayload.question_sharing_id = sharedQuestion?.question_sharing_id || sharedQuestion?.organization_domain_id;
                    }

                    payload = sharedPayload;

                    // Validate required fields before proceeding
                    if (!payload.organization_domain_id) {
                        console.error("Missing organization_domain_id in shared question:", sharedQuestion);
                        console.error("Available keys in sharedQuestion:", Object.keys(sharedQuestion || {}));
                        console.error("domainData structure:", domainData?.map(d => ({
                            id: d?.organization_domain_id,
                            name: d?.domain?.name
                        })));
                        setError('Organization domain mapping not found.');
                        setLoading(false);
                        return;
                    }
                    if (!payload.question_id) {
                        console.error("Missing question_id:", qid);
                        setError('Question ID is missing.');
                        setLoading(false);
                        return;
                    }
                    if (!payload.answer || payload.answer.trim() === "") {
                        console.error("Missing answer - hasAnswer:", hasAnswer, "hasComments:", hasComments, "resp:", resp);
                        setError('Answer is required. Please provide comments or select Yes/No/Partial.');
                        setLoading(false);
                        return;
                    }
                }
            }

            // If not found in shared questions, check in domainData
            if (!payload) {
                // Loop through domains
                for (const domain of domainData || []) {
                    const organizationDomainId = domain?.organization_domain_id;

                    // Loop through controls
                    for (const control of domain?.controls || []) {
                        // Loop through questions
                        for (const q of control?.questions || []) {
                            const qid = q?.control_question_id;

                            // If a specific question is being saved, skip others
                            if (questionId && qid !== questionId) {
                                continue;
                            }

                            const resp = responses[qid];

                            // If we are saving a specific question and no response exists
                            if (questionId && !resp) {
                                setError('Please select Yes / No / Partial before saving.3');
                                setLoading(false);
                                return;
                            }

                            // Skip if no response in bulk mode
                            if (!questionId && !resp) continue;

                            // Check if answer and comments are actually filled
                            const hasAnswer = resp.answer && resp.answer.trim() !== '';
                            const hasComments = resp.comments && resp.comments.trim() !== '';

                            // When saving a specific question, answer is mandatory
                            if (questionId) {
                                const validAnswers = ["Yes", "No", "Partial"];
                                if (!hasAnswer || !validAnswers.includes(resp.answer)) {
                                    setError('Please select Yes / No / Partial before saving.4');
                                    setLoading(false);
                                    return;
                                }
                            }

                            // Skip if both answer and comments are empty
                            if (!hasAnswer && !hasComments) continue;

                            // Build evidences array
                            const evidences = (resp.file_urls || []).map((url) => ({
                                url: url,
                            }));

                            // Create payload
                            if (hasAnswer || hasComments) {
                                // answer field should have comments if available, otherwise use user_response
                                const answerValue = hasComments ? resp.comments : (hasAnswer ? resp.answer : "");

                                payload = {
                                    organization_domain_id: organizationDomainId || null,
                                    question_id: qid,
                                    user_document_id: resp.user_document_id || null,
                                    answer: answerValue,
                                    user_response: hasAnswer ? resp.answer : "",
                                    evidences: evidences.length > 0 ? evidences : [{
                                        url: "",
                                    }]
                                };

                                // Validate required fields
                                if (!payload.organization_domain_id) {
                                    console.error("Missing organization_domain_id for question:", qid);
                                    setError('Organization domain ID is missing. Cannot submit question.');
                                    setLoading(false);
                                    return;
                                }
                                if (!payload.question_id) {
                                    console.error("Missing question_id:", qid);
                                    setError('Question ID is missing.');
                                    setLoading(false);
                                    return;
                                }
                                if (!payload.answer || payload.answer.trim() === "") {
                                    console.error("Missing answer - hasAnswer:", hasAnswer, "hasComments:", hasComments);
                                    setError('Answer is required. Please provide comments or select Yes/No/Partial.');
                                    setLoading(false);
                                    return;
                                }
                            }
                            break;
                        }

                        if (payload) break;
                    }

                    if (payload) break;
                }
            }

            if (!payload) {
                setError('Please answer the question to submit');
                setLoading(false);
                setMessageQuestionId(questionId || null);
                return;
            }

            // Final validation before API call
            if (!payload.organization_domain_id || !payload.question_id || !payload.answer) {
                console.error("Payload validation failed:", payload);
                setError('Missing required fields: organization_domain_id, question_id, or answer');
                setLoading(false);
                setMessageQuestionId(questionId || null);
                return;
            }

            console.log("Submitting payload:", payload);
            const response = await submit_framework_question_answer(payload);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.data?.message ||
                response?.message;

            const errorMessage =
                response?.errors?.message ||
                response?.error?.message ||
                response?.data?.error ||
                null;

            if (successMessage) {
                setResMsg(successMessage);
                setError('');
                setMessageQuestionId(payload?.question_id || questionId || null);
                if (payload?.question_id) {
                    setResponses((prev) => ({
                        ...prev,
                        [payload.question_id]: {
                            ...prev[payload.question_id],
                            submitted: true
                        }
                    }));
                }
            } else if (errorMessage) {
                setError(errorMessage);
                setResMsg('');
                setMessageQuestionId(payload?.question_id || questionId || null);
            } else {
                setError("Unexpected error occurred.");
                setResMsg('');
                setMessageQuestionId(payload?.question_id || questionId || null);
            }
        } catch (error) {
            console.error("Submit Error:", error);
            setError("An error occurred while submitting.");
            setResMsg('');
            setMessageQuestionId(questionId || null);
        } finally {
            setLoading(false);
        }
    }, [domainData, responses, sharedQuestions, submissionStatus]);

    // Handler for domain change - reset control when domain changes
    const handleDomainChange = (domainId) => {
        setSelectedDomainId(domainId);
        setSelectedControlId(""); // Reset control when domain changes
    };

    // Get unique domains for dropdown (only domain names)
    const getUniqueDomains = () => {
        if (!domainData?.length) return [];

        const uniqueDomainsMap = new Map();

        domainData.forEach((domain) => {
            const domainId = domain?.organization_domain_id || domain?.domain_id;
            const domainIdStr = domainId != null ? String(domainId) : "";
            const domainName = domain?.domain?.name || "Untitled Domain";

            // Use domain ID as key to avoid duplicates
            if (domainIdStr && !uniqueDomainsMap.has(domainIdStr)) {
                uniqueDomainsMap.set(domainIdStr, {
                    id: domainIdStr,
                    name: domainName,
                    organization_domain_id: domain?.organization_domain_id,
                    domain_id: domain?.domain_id
                });
            }
        });

        return Array.from(uniqueDomainsMap.values());
    };

    // Get filtered domains based on selectedDomainId (for question filtering)
    const getFilteredDomains = () => {
        if (!selectedDomainId || !domainData?.length) {
            return domainData;
        }
        return domainData.filter((domain) => {
            const domainId = domain?.organization_domain_id || domain?.domain_id;
            const domainIdStr = domainId != null ? String(domainId) : "";
            return domainIdStr === selectedDomainId;
        });
    };

    // Get filtered controls based on selected domain only
    const getFilteredControls = () => {
        if (!domainData?.length) {
            return [];
        }

        // If no domain selected, show all controls from all domains
        if (!selectedDomainId) {
            const allControls = [];
            domainData.forEach((domain) => {
                domain?.controls?.forEach((control) => {
                    allControls.push({ domain, control });
                });
            });
            return allControls;
        }

        // If domain selected, show only that domain's controls
        const filteredDomains = getFilteredDomains();
        const allControls = [];

        filteredDomains.forEach((domain) => {
            domain?.controls?.forEach((control) => {
                allControls.push({ domain, control });
            });
        });

        return allControls;
    };

    // Helper to find the selected control data considering domain filter
    const getSelectedControlData = () => {
        if (!selectedControlId || !domainData?.length) return null;

        const filteredDomains = getFilteredDomains();

        for (const domain of filteredDomains) {
            for (const control of domain?.controls || []) {
                const controlId =
                    control?.organization_domain_control_id || control?.control_id;
                const controlIdStr = controlId != null ? String(controlId) : "";

                if (controlIdStr === selectedControlId) {
                    return { domain, control };
                }
            }
        }
        return null;
    };

    const selectedControlData = getSelectedControlData();
    const filteredDomains = getFilteredDomains();
    const filteredControls = getFilteredControls();
    const uniqueDomains = getUniqueDomains();

    // Get selected domain name for display
    const getSelectedDomainName = () => {
        if (selectedDomainId) {
            const domain = domainData.find((d) => {
                const domainId = d?.organization_domain_id || d?.domain_id;
                const domainIdStr = domainId != null ? String(domainId) : "";
                return domainIdStr === selectedDomainId;
            });
            return domain?.domain?.name || domainData?.[0]?.domain?.name || "";
        }
        return "All";
    };

    // Handle submission button click
    const handleSubmission = async () => {
        const selectionId = localStorage.getItem('selection_id');
        if (!selectionId) {
            setError('Selection ID not found');
            return;
        }

        try {
            setSubmittingStatus(true);
            setError('');
            setResMsg('');

            // Call API with ID in URL path like GET version
            const params = `${selectionId}/submission/`;
            const response = await organizationComplianceSubmissionService(params);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.data?.message ||
                response?.message;

            const errorMessage =
                response?.errors?.message ||
                response?.error?.message ||
                response?.data?.error ||
                null;

            if (successMessage) {
                setModalMessage(successMessage);
                setModalType("success");
                setShowMessageModal(true);
                // Refresh submission status after successful submission
                const statusResponse = await getOrganizationComplianceSubmissionService(params);
                setSubmissionStatus(statusResponse?.statusCode?.response_data?.data?.submission_status || null);
            } else if (errorMessage) {
                setModalMessage(errorMessage);
                setModalType("error");
                setShowMessageModal(true);
            } else {
                setModalMessage("Unexpected error occurred.");
                setModalType("error");
                setShowMessageModal(true);
            }
        } catch (error) {
            console.error("Submission Error:", error);
            setModalMessage("An error occurred while submitting.");
            setModalType("error");
            setShowMessageModal(true);
        } finally {
            setSubmittingStatus(false);
        }
    };

    // Handle set draft status button click
    const handleSetDraftStatus = async () => {
        const selectionId = localStorage.getItem('selection_id');
        if (!selectionId) {
            setError('Selection ID not found');
            return;
        }

        try {
            setSettingDraftStatus(true);
            setError('');
            setResMsg('');

            const response = await setDraftStatusService(selectionId);

            const successMessage =
                response?.statusCode?.response_data?.message ||
                response?.data?.message ||
                response?.message;

            const errorMessage =
                response?.errors?.message ||
                response?.error?.message ||
                response?.data?.error ||
                null;

            if (successMessage) {
                setModalMessage(successMessage);
                setModalType("success");
                setShowMessageModal(true);
                // Refresh submission status after successful status change
                const params = `${selectionId}/submission/`;
                const statusResponse = await getOrganizationComplianceSubmissionService(params);
                setSubmissionStatus(statusResponse?.statusCode?.response_data?.data?.submission_status || null);
            } else if (errorMessage) {
                setModalMessage(errorMessage);
                setModalType("error");
                setShowMessageModal(true);
            } else {
                setModalMessage("Unexpected error occurred.");
                setModalType("error");
                setShowMessageModal(true);
            }
        } catch (error) {
            console.error("Set Draft Status Error:", error);
            setModalMessage("An error occurred while changing status to draft.");
            setModalType("error");
            setShowMessageModal(true);
        } finally {
            setSettingDraftStatus(false);
        }
    };

    return (
        <Container fluid className="py-4" style={{ minHeight: "100vh" }}>
            <div className="d-flex justify-content-between align-items-center">
                {/* <h3 className="color_white mb-4 text-uppercase fw-bold">
                    Domain – {getSelectedDomainName()}
                </h3> */}
                <div className="d-flex align-items-center gap-4">
                    <div className="d-flex align-items-center gap-3 mb-3">
                        {/* <label className="mb-0 fw-semibold text-uppercase small">
                            Domain Name:
                        </label> */}
                        <CommonDropdown
                            value={selectedDomainId}
                            onChange={(val) => handleDomainChange(val)}
                            options={[
                                { value: "", label: "All Domains" },
                                ...uniqueDomains.map((domain) => ({
                                    value: String(domain.id),
                                    label: `${domain.name}`,
                                })),
                            ]}
                            placeholder="All Domains"
                            ariaLabel="Filter by Domain"
                        />
                    </div>
                    <div className="d-flex align-items-center gap-3 mb-3">
                        {/* <label className="mb-0 fw-semibold text-uppercase small">
                            Control Name:
                        </label> */}
                        <CommonDropdown
                            value={selectedControlId}
                            onChange={(val) => setSelectedControlId(val)}
                            options={[
                                { value: "", label: "All Controls" },
                                ...filteredControls.map(({ control }) => ({
                                    value: String(
                                        control?.organization_domain_control_id || control?.control_id
                                    ),
                                    label: `${control?.control_title || "Untitled Control"}`,
                                })),
                            ]}
                            placeholder="All Controls"
                            ariaLabel="Filter by Control"
                        />
                    </div>
                    <div className="d-flex align-items-center gap-3 mb-3">
                        <CommonDropdown
                            value={selectedReviewerResponse}
                            onChange={(val) => setSelectedReviewerResponse(val)}
                            options={[
                                { value: "", label: "All Reviewer Responses" },
                                { value: "Yes", label: "Yes" },
                                { value: "No", label: "No" },
                                { value: "Partial", label: "Partial" },
                            ]}
                            placeholder="All Reviewer Responses"
                            ariaLabel="Filter by Reviewer Response"
                        />
                    </div>
                    {submissionStatus == 'draft' ? (
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <CommonButton
                                text={submittingStatus ? <ButtonLoader /> : "Submit Compliance"}
                                onClick={handleSubmission}
                                className="blue_btn"
                                disabled={submittingStatus}
                            />

                        </div>
                    ) : (
                        <span className="d-flex align-items-center gap-2 mb-3" style={{ color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 500 }}>
                            Current Status:
                            <StatusBadge status={submissionStatus} dot={false}>{submissionStatus}</StatusBadge>
                        </span>
                    )}


                    {submissionStatus == 'rejected' ? (
                        <CommonButton
                            text={settingDraftStatus ? <ButtonLoader /> : "Change Status to Draft"}
                            onClick={handleSetDraftStatus}
                            className="blue_btn mb-3"
                            disabled={settingDraftStatus}
                        />
                    ) : ''}

                    {!isEmptyQue && sharedQuestions.length > 0 && (
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <label className="mb-0" style={{ color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 500 }}>Shared Questions Only</label>
                            <input
                                type="checkbox"
                                checked={showSharedQuestions}
                                onChange={(e) => setShowSharedQuestions(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                            />
                        </div>
                    )}

                </div>
            </div>

            <Row>
                {/* LEFT COLUMN - QUESTIONS */}
                <Col md={8}>
                    <div className="questionnaire_page_wraper">
                        {screenLoader ? (
                            <div className="mt-5 pt-5">
                                <ScreenLoader />
                            </div>
                        ) : showSharedQuestions && sharedQuestions.length > 0 ? (
                            // Show only shared questions when toggle is ON
                            sharedQuestions.map((q, qIndex) => {
                                const uniqueKey = `shared-${q?.control_question_id || qIndex}`;
                                return (
                                    <QuestionCard
                                        key={uniqueKey}
                                        submissionStatus={submissionStatus}
                                        question={q}
                                        questionId={q?.control_question_id}
                                        answered_by={q?.answered_by}
                                        index={qIndex}
                                        domainName={q?.domain_name}
                                        domain={q?.organization}
                                        response={responses[q?.control_question_id]}
                                        onChange={updateResponse}
                                        onFileUpload={handleFileUpload}
                                        onRemoveFile={removeFile}
                                        checkingId={checkingId}
                                        onCheckWithNova={checkWithNovaHandler}
                                        submitQuestionAnswerHandler={submitQuestionAnswerHandler}
                                        resMsg={resMsg}
                                        error={error}
                                        messageQuestionId={messageQuestionId}
                                        controlTitle={q?.control_title || "Shared Question"}
                                        loading={loading}
                                        reviewer_adjustment={q?.reviewer_adjustment}
                                        reviewer_email={q?.reviewer_email}
                                        reviewer_remark={q?.reviewer_remark}
                                        hideShareButton={true}
                                        onRefreshQuestions={fetchQuestionList}
                                    />
                                );
                            })
                        ) : domainData?.length > 0 ? (
                            selectedControlId && selectedControlData ? (
                                // Render questions only for the selected control (considering domain filter)
                                selectedControlData.control?.questions?.map((q, qIndex) => {

                                    const uniqueKey = `selected-${q?.control_question_id || qIndex}`;
                                    return (
                                        <QuestionCard
                                            key={uniqueKey}
                                            submissionStatus={submissionStatus}
                                            question={q}
                                            questionId={q?.control_question_id}
                                            answered_by={q?.answered_by}
                                            shared_with_emails={q?.shared_with_emails}
                                            index={qIndex}
                                            domain={selectedControlData.domain}
                                            domainName={selectedControlData?.domain?.name}
                                            response={responses[q?.control_question_id]}
                                            onChange={updateResponse}
                                            onFileUpload={handleFileUpload}
                                            onRemoveFile={removeFile}
                                            checkingId={checkingId}
                                            onCheckWithNova={checkWithNovaHandler}
                                            submitQuestionAnswerHandler={submitQuestionAnswerHandler}
                                            resMsg={resMsg}
                                            error={error}
                                            messageQuestionId={messageQuestionId}
                                            controlTitle={selectedControlData.control?.control_title}
                                            loading={loading}
                                            reviewer_adjustment={q?.reviewer_adjustment}
                                            reviewer_email={q?.reviewer_email}
                                            reviewer_remark={q?.reviewer_remark}
                                            hideShareButton={false}
                                            onRefreshQuestions={fetchQuestionList}
                                        />
                                    );
                                })
                            ) : selectedControlId && !selectedControlData ? (
                                // Selected control not found
                                <div className="mt-5 pt-5">
                                    <p className="mb-4 text-center" style={{ color: 'var(--color-text-muted)' }}>
                                        No questions found for selected control
                                    </p>
                                </div>
                            ) : (
                                // Show questions based on domain filter (if any) and all controls
                                filteredDomains?.flatMap((domain, domainIndex) =>
                                    domain?.controls?.flatMap((control, controlIndex) =>
                                        control?.questions?.map((q, qIndex) => {
                                            const uniqueKey = `${domainIndex}-${controlIndex}-${q?.control_question_id || qIndex}`;
                                            return (
                                                <QuestionCard
                                                    key={uniqueKey}
                                                    submissionStatus={submissionStatus}
                                                    question={q}
                                                    questionId={q?.control_question_id}
                                                    answered_by={q?.answered_by}
                                                    shared_with_emails={q?.shared_with_emails}
                                                    index={qIndex}
                                                    domain={domain}
                                                    domainName={domain?.domain?.name}
                                                    response={responses[q?.control_question_id]}
                                                    onChange={updateResponse}
                                                    onFileUpload={handleFileUpload}
                                                    onRemoveFile={removeFile}
                                                    checkingId={checkingId}
                                                    onCheckWithNova={checkWithNovaHandler}
                                                    submitQuestionAnswerHandler={submitQuestionAnswerHandler}
                                                    resMsg={resMsg}
                                                    error={error}
                                                    messageQuestionId={messageQuestionId}
                                                    controlTitle={control?.control_title}
                                                    loading={loading}
                                                    reviewer_adjustment={q?.reviewer_adjustment}
                                                    reviewer_email={q?.reviewer_email}
                                                    reviewer_remark={q?.reviewer_remark}
                                                    hideShareButton={false}
                                                    onRefreshQuestions={fetchQuestionList}
                                                />
                                            );
                                        })
                                    )
                                )
                                
                            )
                        ) : (
                            <div className="mt-5 pt-5">
                                <p className="mb-4 text-center" style={{ color: 'var(--color-text-muted)' }}>
                                    No domains assigned to you
                                </p>
                            </div>
                        )}
                    </div>

                </Col>

                {/* RIGHT COLUMN - FORESEE PANEL */}
                <Col md={4}>
                    <NovaPanel
                        novaError={novaError}
                        checkingId={checkingId}
                        novaData={novaData}
                        // responses={responses}
                        selectedQuestionId={selectedQuestionId}
                    />
                </Col>
            </Row>

            {/* Edit Restriction Modal */}
            <CommonModal
                show={showEditRestrictionModal}
                onHide={() => setShowEditRestrictionModal(false)}
                title="Edit Not Allowed"
                buttons={[{ text: "Close", variant: "secondary", onClick: () => setShowEditRestrictionModal(false) }]}
            >
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginBottom: 8 }}>
                    You cannot edit this submission. Only submissions with status "draft" or "rejected" can be edited.
                </p>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, margin: 0 }}>
                    Current status: <strong style={{ color: 'var(--color-text-primary)' }}>{submissionStatus || "N/A"}</strong>
                </p>
            </CommonModal>

            {/* Message Modal */}
            <CommonModal
                show={showMessageModal}
                onHide={() => setShowMessageModal(false)}
                title={modalType === "success" ? "Success" : "Error"}
                buttons={[{ text: "Close", variant: modalType === "success" ? "primary" : "danger", onClick: () => setShowMessageModal(false) }]}
            >
                <p style={{ color: modalType === "success" ? 'var(--color-success)' : 'var(--color-danger)', fontSize: 13, margin: 0 }}>
                    {modalMessage}
                </p>
            </CommonModal>

        </Container>
    );
}

export default Questionnaire;
